/**
 * Explicações dos cards do Painel Comercial — o texto do "?" e a régua curta
 * sob o título. Indexadas pelo id do card no catálogo (`_catalogoComercial`),
 * separadas do desenho do card para poderem ser revisadas todas de uma vez.
 *
 * Cada texto descreve a conta em `comercial/metricas.py` (backend) e as
 * réguas do ADR-0079. Se a conta mudar lá, o texto muda aqui junto.
 *
 * Formato de cada entrada:
 *   regua       — lembrete curto sob o título (quem já sabe ler o card);
 *   oQueE       — 1 frase: que pergunta o número responde;
 *   comoContado — 1 a 5 passos curtos, na ordem da conta;
 *   exemplo     — números inventados, com a conta até o que o card mostra;
 *   atencao     — só quando há armadilha real (opcional).
 *
 * Cards cuja conta muda com a "Fonte do valor" (configurar card) declaram
 * `fonte` (a chave gravada em layout_painel.fontes) e `variantes`: o texto
 * base descreve a fonte PADRÃO do sistema; cada variante sobrescreve só os
 * campos que mudam. `atencao: null` numa variante remove o aviso da base.
 */

// Fonte que o backend usa quando o gestor não escolheu nenhuma — é também a
// opção recomendada na tela (comercial/metricas.py: *_FONTE_DEFAULT).
export const FONTE_PADRAO = {
  pipeline: "lead_orcamento_fallback",
  receita: "orcamento_ganho",
  taxa_conversao: "orcamento",
  ticket_medio: "orcamento",
  perda: "lead",
  vendedores: "lead",
  funil: "lead",
};

// Frases repetidas em vários cards — uma fonte só para não divergirem.
const PASSO_SETA =
  "A seta compara com o mesmo trecho do período anterior (no Mês, de 01 a 05/10 contra 01 a 05/09).";
const PASSO_ESTOQUE = "Não segue o período: é sempre o que está em aberto agora.";
const LEAD_ABERTO =
  "Lead aberto é o que não foi arquivado, não foi perdido e não está numa etapa do tipo Finalizado (etapa do tipo Parado conta).";
const VENDAS_GANHAS_NO_PERIODO =
  "Pega as vendas ganhas no período: orçamentos Aprovado pelo cliente ou Convertido em pedido, no dia da aprovação.";
const SO_ORCAMENTO_DO_SISTEMA =
  "Depende de orçamentos feitos no sistema: orçamento feito fora dele não entra, e sem nenhum o card avisa em vez de mostrar 0.";

export const EXPLICACOES = {
  pipeline: {
    fonte: "pipeline",
    regua: "orçamento aberto do lead ou, sem ele, o valor estimado",
    oQueE: "Quanto dinheiro está em negociação agora e ainda pode virar venda.",
    comoContado: [
      "Para cada lead aberto, usa o orçamento mais recente dele se estiver em Rascunho ou Em negociação.",
      "Lead sem orçamento, ou com o orçamento recusado ou expirado, entra pelo Valor estimado.",
      "Lead com orçamento já aprovado sai: aquilo já é venda e conta na Receita realizada.",
      LEAD_ABERTO,
      PASSO_ESTOQUE,
    ],
    exemplo:
      "Lead 101 tem orçamento em negociação de R$ 70.000: conta R$ 70.000. Lead 102 não tem orçamento e tem valor estimado de R$ 30.000: conta R$ 30.000. Lead 103 teve o orçamento aprovado: não conta. Pipeline aberto: R$ 100k.",
    atencao:
      "Não tem seta: o sistema não guarda como o pipeline estava antes, então não há com o que comparar.",
    variantes: {
      orcamento: {
        regua: "soma dos orçamentos em Rascunho e Em negociação",
        comoContado: [
          "Soma o valor total dos orçamentos em Rascunho e Em negociação.",
          "Aprovado pelo cliente não entra: já é venda e conta na Receita realizada.",
          "Convertido em pedido, Recusado e Expirado também ficam fora.",
          PASSO_ESTOQUE,
        ],
        exemplo:
          "Há R$ 40.000 em rascunho, R$ 60.000 em negociação e R$ 20.000 aprovado. Pipeline aberto: R$ 40.000 + R$ 60.000 = R$ 100k.",
      },
      lead_manual: {
        regua: "soma do valor estimado dos leads abertos",
        comoContado: [
          "Soma o Valor estimado dos leads abertos do CRM.",
          LEAD_ABERTO,
          "Lead sem Valor estimado entra como zero.",
          PASSO_ESTOQUE,
        ],
        exemplo:
          "Ana tem 2 leads abertos, de R$ 30.000 e R$ 50.000, e Pedro tem 1 lead sem valor. Pipeline aberto: R$ 80k.",
      },
    },
  },

  receita_realizada: {
    fonte: "receita",
    regua: "orçamentos ganhos no período",
    oQueE: "Quanto foi vendido no período.",
    comoContado: [
      "Soma o valor total dos orçamentos Aprovado pelo cliente ou Convertido em pedido.",
      "Conta a venda no dia em que foi aprovada (ou convertida, se virou pedido direto), não no dia em que o orçamento foi criado.",
      "A barra compara com a meta de Comercial › Configurações. A meta é mensal: na Semana vale a parte de 7 dias, no Trimestre 3 meses, no Ano 12 meses.",
      "No período Mês, a linha de baixo mostra quanto o mês anterior inteiro fechou.",
      PASSO_SETA,
    ],
    exemplo:
      "Com meta de R$ 100.000 por mês, no trimestre foram aprovados R$ 150.000: a meta do trimestre é R$ 300.000 e a barra mostra 50%.",
    variantes: {
      pedido_entregue: {
        regua: "pedidos entregues no período",
        comoContado: [
          "Soma o valor dos pedidos com situação Entregue.",
          "Conta o pedido no dia da entrega.",
          "A barra compara com a meta de Comercial › Configurações. A meta é mensal: na Semana vale a parte de 7 dias, no Trimestre 3 meses, no Ano 12 meses.",
          "No período Mês, a linha de baixo mostra quanto o mês anterior inteiro fechou.",
          PASSO_SETA,
        ],
        exemplo:
          "Em setembro foram entregues 2 pedidos, de R$ 45.000 e R$ 25.000, com meta de R$ 100.000: receita de R$ 70k e 70% da meta do mês.",
      },
      lead_manual_finalizado: {
        regua: "valor estimado dos leads ganhos no período",
        comoContado: [
          "Soma o Valor estimado dos leads que foram para uma etapa do tipo Finalizado.",
          "Conta o lead no dia em que ele foi para essa etapa. Lead arquivado não entra.",
          "A barra compara com a meta de Comercial › Configurações. A meta é mensal: na Semana vale a parte de 7 dias, no Trimestre 3 meses, no Ano 12 meses.",
          "No período Mês, a linha de baixo mostra quanto o mês anterior inteiro fechou.",
          PASSO_SETA,
        ],
        exemplo:
          "Em setembro, 3 leads foram finalizados, de R$ 20.000, R$ 35.000 e R$ 25.000, com meta de R$ 100.000: receita de R$ 80k e 80% da meta do mês.",
      },
    },
  },

  taxa_conversao: {
    fonte: "taxa_conversao",
    regua: "orçamentos enviados no período que viraram venda",
    oQueE: "De cada 100 orçamentos enviados ao cliente, quantos viraram venda.",
    comoContado: [
      "Pega os orçamentos enviados ao cliente no período, pelo dia do envio.",
      "Divide os que hoje estão Aprovado pelo cliente ou Convertido em pedido pelo total enviado.",
      PASSO_SETA,
    ],
    exemplo:
      "No mês foram enviados 20 orçamentos e 5 deles já foram aprovados: taxa de 25%.",
    atencao:
      "A seta é em pontos: de 20% para 25% é 5pt, e não 25%. " + SO_ORCAMENTO_DO_SISTEMA,
    variantes: {
      leads: {
        regua: "leads finalizados ÷ todos os leads",
        comoContado: [
          "Divide os leads em etapas do tipo Finalizado pelo total de leads não arquivados da unidade.",
          "Não segue o período: conta os leads de qualquer data. Por isso não tem seta nem linha.",
          "Lead perdido continua no total e não conta como finalizado.",
        ],
        exemplo:
          "A unidade tem 200 leads não arquivados e 30 estão em etapa do tipo Finalizado: taxa de 15%.",
        atencao: "Não é a taxa do período: muda devagar, porque soma o histórico inteiro da unidade.",
      },
    },
  },

  ticket_medio: {
    fonte: "ticket_medio",
    regua: "valor das vendas ganhas ÷ quantidade",
    oQueE: "Quanto vale, em média, cada venda fechada.",
    comoContado: [
      VENDAS_GANHAS_NO_PERIODO,
      "Soma o valor total delas e divide pela quantidade (média simples).",
      PASSO_SETA,
    ],
    exemplo:
      "Foram ganhas 4 vendas, de R$ 20.000, R$ 30.000, R$ 40.000 e R$ 70.000: R$ 160.000 ÷ 4 = ticket de R$ 40k.",
    atencao: SO_ORCAMENTO_DO_SISTEMA,
    variantes: {
      lead_finalizado: {
        regua: "valor estimado dos leads ganhos ÷ quantidade",
        comoContado: [
          "Pega os leads que foram para uma etapa do tipo Finalizado no período (lead arquivado não entra).",
          "Soma o Valor estimado deles e divide pela quantidade.",
          "Lead sem Valor estimado entra como zero e puxa a média para baixo.",
          PASSO_SETA,
        ],
        exemplo:
          "3 leads finalizados: R$ 30.000, R$ 60.000 e um sem valor. R$ 90.000 ÷ 3 = ticket de R$ 30k.",
        atencao: null,
      },
    },
  },

  tempo_resposta: {
    regua: "horas até o 1º atendimento",
    oQueE: "Quanto tempo, em média, um lead novo espera até ser atendido.",
    comoContado: [
      "Pega os leads criados no período.",
      "Para cada um, mede as horas entre a chegada do lead e o primeiro atendimento registrado nele.",
      "Faz a média simples. Lead ainda sem atendimento não entra; se nenhum foi atendido, o card mostra —.",
      "O alvo vem de Comercial › Configurações (padrão 4 h).",
      PASSO_SETA,
    ],
    exemplo:
      "3 leads novos foram atendidos em 2 h, 4 h e 9 h: 15 ÷ 3 = tempo de resposta de 5,0 h.",
    atencao:
      "Só conta atendimento registrado no sistema: lead atendido por telefone sem registro fica fora da média.",
  },

  tempo_medio_aprovacao: {
    regua: "dias do envio do orçamento à aprovação",
    oQueE: "Quantos dias, em média, o cliente leva para aprovar um orçamento depois de recebê-lo.",
    comoContado: [
      VENDAS_GANHAS_NO_PERIODO,
      "Para cada uma, conta os dias entre o envio ao cliente e a aprovação. Sem data de envio ou de aprovação, fica de fora.",
      "Faz a média simples. Sem nenhuma aprovação no período, o card mostra —.",
      PASSO_SETA,
    ],
    exemplo:
      "3 orçamentos foram aprovados 2, 4 e 9 dias depois do envio: 15 ÷ 3 = lead time de 5,0 d.",
    atencao:
      "Não começa na chegada do lead: começa no envio do orçamento. " + SO_ORCAMENTO_DO_SISTEMA,
  },

  margem_media: {
    regua: "margem das vendas ganhas",
    oQueE: "Qual a margem de lucro, em média, das vendas fechadas.",
    comoContado: [
      VENDAS_GANHAS_NO_PERIODO,
      "Faz a média simples da margem de lucro (%) gravada em cada uma: uma venda pequena pesa igual a uma grande.",
      "Sem nenhuma venda no período, o card mostra — e \"sem vendas no período\".",
      PASSO_SETA,
    ],
    exemplo:
      "2 vendas ganhas: R$ 10.000 com 30% e R$ 90.000 com 20%. (30 + 20) ÷ 2 = margem média de 25,0%.",
    atencao:
      "A seta é em pontos: de 25% para 27% é 2pt, e não 8%. " + SO_ORCAMENTO_DO_SISTEMA,
  },

  aprovacoes_fora_limite: {
    regua: "orçamentos enviados com desconto acima do limite",
    oQueE: "Quantos orçamentos enviados no período deram desconto acima do limite permitido.",
    comoContado: [
      "Pega os orçamentos enviados ao cliente no período, pelo dia do envio (Rascunho não entra).",
      "Conta os que têm desconto maior que o Desconto máx sem aprovação (%) de Comercial › Configurações (10% se a unidade não configurou).",
      PASSO_SETA,
    ],
    exemplo:
      "Com limite de 10%, o mês teve orçamentos enviados com 5%, 12% e 15% de desconto: 2 acima do limite.",
    atencao:
      "Não diz se o desconto foi aprovado: o sistema não registra essa aprovação, só o pedido por e-mail. " +
      SO_ORCAMENTO_DO_SISTEMA,
  },

  valor_ponderado: {
    regua: "orçamentos abertos × probabilidade",
    oQueE: "Quanto do pipeline deve virar venda, pesando cada orçamento pela chance de fechar.",
    comoContado: [
      "Pega os orçamentos em Rascunho e Em negociação (Aprovado já é venda e fica fora).",
      "Multiplica o valor total de cada um pela probabilidade (%) do orçamento e soma.",
      "Orçamento sem probabilidade preenchida entra pelo valor cheio (100%).",
      PASSO_ESTOQUE,
    ],
    exemplo:
      "Um orçamento de R$ 100.000 com 50% e outro de R$ 40.000 com 25%: R$ 50.000 + R$ 10.000 = R$ 60k.",
    atencao:
      "Sempre usa os orçamentos, mesmo que o Pipeline aberto use o valor dos leads. " +
      SO_ORCAMENTO_DO_SISTEMA,
  },

  comissao_prevista: {
    regua: "comissão das vendas ganhas no período",
    oQueE: "Quanto de comissão (RT) as vendas do período vão gerar.",
    comoContado: [
      VENDAS_GANHAS_NO_PERIODO,
      "Para cada uma, calcula valor total × % de comissão: a soma dos parceiros do orçamento, ou a comissão do próprio orçamento, ou a do representante do cliente.",
      "Soma tudo. Orçamento sem vendedor não entra.",
    ],
    exemplo:
      "2 vendas ganhas: R$ 50.000 com 5% e R$ 30.000 com 10%. R$ 2.500 + R$ 3.000 = R$ 5.500,00.",
    atencao: SO_ORCAMENTO_DO_SISTEMA,
  },

  leads_sem_orcamento: {
    regua: "leads abertos sem nenhuma proposta",
    oQueE: "Quantas oportunidades abertas ainda não receberam orçamento.",
    comoContado: [
      "Pega os leads abertos do CRM.",
      LEAD_ABERTO,
      "Conta os que não têm nenhum orçamento ligado.",
      PASSO_ESTOQUE,
    ],
    exemplo: "Há 40 leads abertos e 28 já têm orçamento: 12 leads sem orçamento.",
    atencao:
      "Orçamento feito fora do sistema não é visto: se a unidade não faz orçamento no sistema, o card avisa em vez de contar todos os leads.",
  },

  valor_por_etapa: {
    fonte: "pipeline",
    regua: "valor dos leads abertos em cada etapa",
    oQueE: "Em que etapa do CRM está o dinheiro que ainda pode virar venda.",
    comoContado: [
      "Para cada lead aberto, usa o mesmo valor do Pipeline aberto: orçamento em Rascunho ou Em negociação, senão o Valor estimado.",
      "Lead com orçamento já aprovado sai (já é venda).",
      "Soma por etapa e mostra quantos leads há em cada uma. A barra maior é a etapa com mais valor.",
      PASSO_ESTOQUE,
    ],
    exemplo:
      "Na etapa Proposta há 2 leads: um com orçamento em negociação de R$ 60.000 e outro sem orçamento, com valor estimado de R$ 20.000. A linha mostra R$ 80.000,00 · 2 leads.",
    atencao: null,
    variantes: {
      lead_manual: {
        comoContado: [
          "Para cada lead aberto, usa o Valor estimado do lead (sem valor, entra como zero).",
          "Soma por etapa e mostra quantos leads há em cada uma. A barra maior é a etapa com mais valor.",
          PASSO_ESTOQUE,
        ],
        exemplo:
          "Na etapa Proposta há 2 leads, com valor estimado de R$ 60.000 e R$ 20.000. A linha mostra R$ 80.000,00 · 2 leads.",
      },
      orcamento: {
        atencao:
          "Aqui a conta é por lead e o Pipeline aberto está somando só orçamentos: a soma das etapas pode não bater com ele.",
      },
    },
  },

  funil: {
    fonte: "funil",
    regua: "leads que chegaram no período, etapa por etapa do CRM",
    oQueE: "Dos leads que chegaram no período, até onde cada um avançou no CRM.",
    comoContado: [
      "Pega os leads criados no período.",
      "Uma barra por etapa do CRM, na ordem do board. Cada barra conta os leads que estão nela ou numa etapa mais à frente.",
      "A última barra, Fechados, são os que estão numa etapa do tipo Finalizado. A % é sobre o total de leads do período.",
      "Perdidos são os leads marcados como perdidos. Maior perda é a passagem entre barras que perdeu mais leads.",
    ],
    exemplo:
      "Chegaram 100 leads: 100 em Contato, 60 em Visita, 30 em Proposta e 5 Fechados, e 20 foram marcados como perdidos. Maior perda: Contato → Visita (40 não passaram); 5,0% chegam ao fim.",
    atencao:
      "O CRM não guarda por onde o lead passou: lead parado ou perdido numa etapa conta até ela.",
    variantes: {
      orcamento: {
        regua: "orçamentos criados no período, passo a passo",
        oQueE: "Dos orçamentos criados no período, quantos avançaram até virar pedido.",
        comoContado: [
          "Pega os orçamentos criados no período.",
          "Barras: Orçamentos criados → Enviados ao cliente → Aprovados → Convertidos em pedido. Cada uma é um pedaço da anterior.",
          "A % é sobre os orçamentos criados. Perdidos são os Recusados ou Expirados.",
        ],
        exemplo:
          "40 orçamentos criados, 30 enviados, 12 aprovados e 8 convertidos em pedido: 20,0% chegam ao fim.",
        atencao: SO_ORCAMENTO_DO_SISTEMA,
      },
    },
  },

  por_que_perdemos: {
    fonte: "perda",
    regua: "motivos das perdas registradas no período",
    oQueE: "Por quais motivos os negócios foram perdidos no período.",
    comoContado: [
      "Pega os leads marcados como perdidos no período, pelo dia em que a perda foi registrada. Perda desfeita sai da conta.",
      "Agrupa pelo motivo escolhido ao registrar a perda; a % de cada motivo é sobre o total de perdidos.",
      "A barra fica vermelha a partir de 25% e laranja a partir de 12%.",
      "No topo: quantos foram perdidos, a soma do Valor estimado deles e a % de perda (perdidos ÷ ganhos + perdidos).",
    ],
    exemplo:
      "No mês, 20 leads foram perdidos: 8 por Achou caro (40%, vermelha), 3 por Prazo de entrega muito longo (15%, laranja) e 9 por Cliente sumiu (45%, vermelha).",
    atencao: "Lead perdido sem motivo aparece como Sem motivo.",
    variantes: {
      orcamento: {
        regua: "razões de perda dos orçamentos no período",
        comoContado: [
          "Pega as razões de perda registradas nos orçamentos no período e agrupa por razão.",
          "A % de cada razão é sobre o total de razões registradas.",
          "A barra fica vermelha a partir de 25% e laranja a partir de 12%.",
          "No topo: orçamentos recusados ou expirados no período, o valor deles e a % de perda (perdidos ÷ ganhos + perdidos).",
        ],
        exemplo:
          "No mês foram registradas 10 razões: 5 de preço (50%, vermelha), 2 de prazo (20%, laranja) e 3 de concorrência (30%, vermelha).",
        atencao: SO_ORCAMENTO_DO_SISTEMA,
      },
    },
  },

  ranking_vendedores: {
    fonte: "vendedores",
    regua: "valor estimado dos leads ganhos, por responsável",
    oQueE: "Quem mais vendeu no período, em valor.",
    comoContado: [
      "Para cada responsável, soma o Valor estimado dos leads que ele levou a uma etapa do tipo Finalizado no período.",
      "Deals = leads ganhos. Conv. = ganhos ÷ (ganhos + perdidos no período). Ticket = valor ganho ÷ ganhos.",
      "Mostra os 5 primeiros, do maior valor para o menor.",
      "A seta compara o valor ganho com o mesmo trecho do período anterior; \"novo\" = não vendeu nada antes.",
    ],
    exemplo:
      "Ana fechou 3 leads (R$ 20.000, R$ 30.000 e R$ 40.000) e perdeu 1: R$ 90k, 3 deals, conv. 75% e ticket R$ 30k.",
    atencao: "Lead sem responsável não aparece no ranking.",
    variantes: {
      orcamento: {
        regua: "valor das vendas ganhas, por vendedor",
        comoContado: [
          "Para cada vendedor, soma o valor das vendas ganhas no período (dia da aprovação).",
          "Deals = vendas ganhas no período. Conv. = dos orçamentos que ele enviou no período, quantos viraram venda. Ticket = valor ganho ÷ deals.",
          "Mostra os 5 primeiros, do maior valor para o menor.",
          "A seta compara o valor ganho com o mesmo trecho do período anterior; \"novo\" = não vendeu nada antes.",
        ],
        exemplo:
          "Pedro enviou 4 orçamentos no mês e 2 deles foram aprovados, de R$ 50.000 e R$ 30.000: R$ 80k, 2 deals, conv. 50% e ticket R$ 40k.",
        atencao: "Orçamento sem vendedor não aparece no ranking. " + SO_ORCAMENTO_DO_SISTEMA,
      },
    },
  },

  valor_perdido: {
    fonte: "perda",
    regua: "valor estimado dos leads perdidos no período",
    oQueE: "Quanto dinheiro em negociação foi perdido no período.",
    comoContado: [
      "Soma o Valor estimado dos leads marcados como perdidos no período.",
      "Conta pelo dia em que a perda foi registrada. Perda desfeita sai da conta.",
      "Lead perdido sem Valor estimado entra na quantidade, mas soma zero.",
    ],
    exemplo:
      "No mês foram perdidos 3 leads: R$ 20.000, R$ 15.000 e um sem valor. Valor perdido de R$ 35k, 3 leads perdidos.",
    variantes: {
      orcamento: {
        regua: "valor dos orçamentos perdidos no período",
        comoContado: [
          "Soma o valor total dos orçamentos Recusados ou Expirados.",
          "Conta pelo dia da recusa ou da expiração.",
        ],
        exemplo:
          "2 orçamentos foram recusados no mês, de R$ 25.000 e R$ 15.000: valor perdido de R$ 40k.",
        atencao: SO_ORCAMENTO_DO_SISTEMA,
      },
    },
  },

  taxa_perda: {
    fonte: "perda",
    regua: "perdidos ÷ (ganhos + perdidos)",
    oQueE: "De cada 100 negócios que terminaram no período, quantos foram perdidos.",
    comoContado: [
      "Conta os leads perdidos no período (dia do registro da perda) e os ganhos no período (dia em que foram para uma etapa do tipo Finalizado).",
      "Divide os perdidos pela soma de ganhos e perdidos.",
      "Lead ainda aberto não entra. Sem nenhum ganho nem perda no período, mostra 0%.",
    ],
    exemplo: "No mês, 6 leads foram perdidos e 4 foram ganhos: 6 ÷ 10 = 60%.",
    atencao:
      "Não é o contrário da Taxa conversão: aquela olha os orçamentos enviados, esta só os negócios que já terminaram.",
    variantes: {
      orcamento: {
        regua: "orçamentos perdidos ÷ (ganhos + perdidos)",
        comoContado: [
          "Conta os orçamentos recusados ou expirados no período e as vendas ganhas no período.",
          "Divide os perdidos pela soma de ganhos e perdidos.",
          "Orçamento ainda em Rascunho ou Em negociação não entra.",
        ],
        exemplo:
          "No mês, 3 orçamentos foram recusados, 1 expirou e 4 foram aprovados: 4 ÷ 8 = 50%.",
      },
    },
  },

  motivos_perda: {
    fonte: "perda",
    regua: "motivos de perda por valor ou por quantidade",
    oQueE: "Quais motivos de perda mais custaram no período, em dinheiro ou em negócios.",
    comoContado: [
      "Pega os leads marcados como perdidos no período e agrupa pelo motivo da perda.",
      "Para cada motivo mostra quantos leads, a % sobre o total de perdidos e a soma do Valor estimado.",
      "O seletor Valor | Qtd escolhe a ordem e o tamanho da barra. Ele não fica salvo: ao recarregar volta para Valor.",
      "Mostra até 8 motivos.",
    ],
    exemplo:
      "10 leads perdidos: 6 por Achou caro, somando R$ 60.000, e 4 por Prazo de entrega muito longo, somando R$ 90.000. Em Valor, prazo vem primeiro (R$ 90k); em Qtd, Achou caro vem primeiro (6 leads · 60%).",
    variantes: {
      orcamento: {
        regua: "razões de perda dos orçamentos (quantidade)",
        comoContado: [
          "Pega as razões de perda registradas nos orçamentos no período e agrupa por razão.",
          "Os orçamentos não guardam valor por razão: só dá para ordenar por Qtd.",
          "Mostra até 8 razões, da mais frequente para a menos.",
        ],
        exemplo: "5 razões registradas: 3 de preço (60%) e 2 de prazo (40%).",
        atencao: SO_ORCAMENTO_DO_SISTEMA,
      },
    },
  },
};

// Explicação pronta para exibir: base + variante da fonte em uso.
// `fontes` = fontes em uso da unidade (pode vir vazio).
export function resolverExplicacao(id, fontes = {}) {
  const base = EXPLICACOES[id];
  if (!base) return null;
  const { variantes, fonte, ...texto } = base;
  let junto = texto;
  if (fonte) {
    const fonteEmUso = (fontes && fontes[fonte]) || FONTE_PADRAO[fonte];
    const variante = variantes?.[fonteEmUso];
    if (variante) junto = { ...texto, ...variante };
  }
  if (junto.atencao == null) {
    const { atencao, ...semAtencao } = junto;
    return semAtencao;
  }
  return junto;
}
